import type { JsonMap } from '../types/delivery-runs.types';

export type PayloadFieldChange = 'added' | 'removed' | 'changed' | 'unchanged';

/** One top-level payload field compared across two deliveries. */
export interface PayloadFieldDiff {
  field: string;
  before: string | null;
  after: string | null;
  change: PayloadFieldChange;
}

/** Stable display string for a payload value (objects/arrays as compact JSON). */
function show(value: unknown): string {
  if (value === null || value === undefined) return '';
  return typeof value === 'string' ? value : JSON.stringify(value);
}

/**
 * The payload as sent: drops the backend's reserved `_`-prefixed keys (e.g.
 * `_identity`, the product's title / image kept for display when an attribute
 * selection filtered them out), which were stored alongside but never delivered.
 */
export function sentPayload(payload: JsonMap): JsonMap {
  return Object.fromEntries(Object.entries(payload).filter(([key]) => !key.startsWith('_')));
}

/**
 * Field-by-field diff of two delivered payloads (`transformedData`), top-level
 * keys only — nested values compare as their JSON. Sorted changed-first so the
 * reader sees what moved between deliveries before what didn't.
 */
export function diffPayloads(rawBefore: JsonMap, rawAfter: JsonMap): PayloadFieldDiff[] {
  const before = sentPayload(rawBefore);
  const after = sentPayload(rawAfter);
  const fields = [...new Set([...Object.keys(before), ...Object.keys(after)])];
  const rows = fields.map((field): PayloadFieldDiff => {
    const has = { b: field in before, a: field in after };
    const b = has.b ? show(before[field]) : null;
    const a = has.a ? show(after[field]) : null;
    const change: PayloadFieldChange = !has.b
      ? 'added'
      : !has.a
        ? 'removed'
        : b === a
          ? 'unchanged'
          : 'changed';
    return { field, before: b, after: a, change };
  });
  const order: Record<PayloadFieldChange, number> = {
    changed: 0,
    added: 1,
    removed: 2,
    unchanged: 3,
  };
  return rows.sort((x, y) => order[x.change] - order[y.change] || x.field.localeCompare(y.field));
}
